export { jobFromLead, postcodeFromAddress, windowsFromWords, type JobDraft } from './enquiry-quote-facts'
// "Write a quote" on New jobs (quotes by email, step 2; plan docs/plans/next-level/plan.html): the pure
// half. Same shape as enquiry-message.ts: the reply's wording lives here and nowhere else, and
// scripts/write-quote.test.mjs pins it. Every price comes from quote() in functions/lib/price-list.ts,
// the one price sum; nothing here works a price out. No AI: the owner fills in the job.
//
// The voice is Dropby's (the first round), kept for every round: "Hi Sarah," then thanks, the price
// (a per-window round names the rate that applied first), the first clean, one next step, and the
// round's booking sign-off on one line. Plain text, UK English, no em dashes, whole pounds when whole.
// A customer is never told their price depends on the area: no postcode gives a range, unexplained.

import { currencySymbol, roundConfig } from './round-config'
import { businessSettings, type BusinessSettings } from './business-settings'
import { firstName } from './late-message'
import { BOARD_COLUMNS } from './new-jobs'
import type { Lead } from './types'
import { postcodeStart, windowRate, type HowOften, type Job, type PriceList, type Quote } from '../functions/lib/price-list'
import { kindLabel } from './trade'
import { money } from './ui'

// The app's currency and thousands separators, keeping whole-pound quote wording compact.
export function quotePounds(pence: number): string {
  return money(pence).replace(/\.00$/, '')
}

// "£40", or "around £35 to £45" when the postcode is missing and the areas differ. The panel shows the
// same words the reply uses.
export function quoteAmount(pence: number, range: [number, number] | null): string {
  return range ? `around ${quotePounds(range[0])} to ${quotePounds(range[1])}` : quotePounds(pence)
}

const OFTEN_WORDS: Record<HowOften, string> = {
  w2: 'every 2 weeks',
  w4: 'every 4 weeks',
  w6: 'every 6 weeks',
  w8: 'every 8 weeks',
  w12: 'every 12 weeks',
  one_off: '',
}

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`

// What is being priced, as the customer would say it: "your 14 windows", "the 9 windows at the front",
// "a 3 bed house with 2 roof windows".
function whatWords(list: PriceList, job: Job): string {
  // By size (15 Oct 2026): "your patio and driveway (40 m²)", in the round's own words for the kind of job.
  if (list.method === 'by_area') return `your ${kindLabel(job.kind === 'gutter' || job.kind === 'other' ? job.kind : 'window').toLowerCase()} (${Math.round((job.sqm ?? 0) * 10) / 10} m²)`
  const roof = Math.max(0, Math.floor(job.roof_windows ?? 0))
  if (list.method === 'per_window') {
    const windows = Math.floor(job.windows ?? 0)
    const base = job.fronts_only ? `the ${plural(windows, 'window')} at the front` : `your ${plural(windows, 'window')}`
    return roof > 0 ? `${base} and ${plural(roof, 'roof window')}` : base
  }
  const label = String(job.size ?? '').trim()
  // "3 bed" -> "a 3 bed house"; "Flat" -> "a flat".
  const home = /\bbed/i.test(label) ? `a ${label} house` : `a ${label.toLowerCase()}`
  const base = job.fronts_only ? `the front windows of ${home}` : home
  return roof > 0 ? `${base} with ${plural(roof, 'roof window')}` : base
}

// The reply, ready to edit and send, or null when the job still needs details (no window count, no house
// size): no price may go out then. `insides` adds the insides price when the list has one.
export function quoteMessage(
  name: string | null | undefined,
  list: PriceList,
  job: Job,
  q: Quote,
  // Write a quote B (27 Oct 2026): insides included (every visit or the first only), Ad hoc's words, and the first visit's
  // extras with what the first visit comes to.
  opts: { insides?: boolean; insidesEvery?: boolean; insidesIncluded?: boolean; adhoc?: boolean; extras?: { name: string; pence: number }[] } = {},
  settings: BusinessSettings = businessSettings(),
): string | null {
  if (q.status !== 'priced') return null
  const first = firstName(name)
  const greeting = first ? `Hi ${first},` : 'Hi,'
  const oneOff = job.how_often === 'one_off'
  // A one-off is a single first clean, so its price is the first-clean price.
  const price = oneOff
    ? opts.adhoc ? `${quoteAmount(q.first_pence, q.first_range_pence)} each time, whenever you'd like us` : `${quoteAmount(q.first_pence, q.first_range_pence)} for a one-off clean`
    : `${quoteAmount(q.regular_pence, q.regular_range_pence)} a visit ${OFTEN_WORDS[job.how_often]}`
  const what = whatWords(list, job)
  const sentences: string[] = ['Thanks for getting in touch.']
  const ranged = oneOff ? q.first_range_pence : q.regular_range_pence
  if (list.method === 'by_area') {
    // A job by size is quoted as one price for the job, never "a visit" (15 Oct 2026).
    sentences.push(`For ${what} it's ${quoteAmount(q.first_pence, q.first_range_pence)}.`)
  } else if (list.method === 'per_window' && !ranged) {
    sentences.push(`We charge ${quotePounds(windowRate(list, job, roundConfig().country).rate)} a window, so for ${what} that's ${price}.`)
  } else {
    // A range has no one rate to name: the price alone, without saying why it is a range.
    sentences.push(`For ${what} it's ${price}.`)
  }
  const sameFirst = q.first_pence === q.regular_pence && !q.first_range_pence && !q.regular_range_pence
  if (!oneOff && !sameFirst && list.method !== 'by_area') sentences.push(`The first clean is ${quoteAmount(q.first_pence, q.first_range_pence)} as it takes a bit longer.`)
  if (opts.insidesIncluded && q.inside_pence != null) {
    sentences.push(opts.insidesEvery && !oneOff ? `The insides are ${quoteAmount(q.inside_pence, q.inside_range_pence)} each visit as well.` : `The insides are ${quoteAmount(q.inside_pence, q.inside_range_pence)}${oneOff ? '' : ' on the first visit'}.`)
  } else if (opts.insides && q.inside_pence != null) {
    sentences.push(`If you'd like the insides done as well, that's ${quoteAmount(q.inside_pence, q.inside_range_pence)} extra${oneOff ? '' : ' each visit'}.`)
  }
  const extras = (opts.extras ?? []).filter((e) => e.pence > 0 && e.name.trim())
  if (extras.length) sentences.push(`${oneOff ? 'As well' : 'On the first visit'}, ${extras.map((e) => `${e.name.trim().toLowerCase()} ${quotePounds(e.pence)}`).join(' and ')}.`)
  // What the first visit comes to, when more than one price is on it (Astra's check of design B).
  if ((extras.length || opts.insidesIncluded) && !q.first_range_pence && !q.inside_range_pence) {
    const firstTotal = q.first_pence + (opts.insidesIncluded && q.inside_pence != null ? q.inside_pence : 0) + extras.reduce((n, e) => n + e.pence, 0)
    sentences.push(`So ${oneOff ? 'it' : 'your first visit'} comes to ${quotePounds(firstTotal)}.`)
  }
  return `${greeting}\n\n${sentences.join(' ')}\n\nHappy to book you in, just let me know a day that suits.\n\nKind regards, ${settings.booking_signoff}`
}

// The fields a sent quote saves on the lead. A figure that is still a range (no postcode) saves as blank:
// the lead keeps one price per column, a guess at one end would be wrong half the time, and an older exact
// price left in place would no longer be what was sent (Astra, 1 Oct).
export function quoteSaveFields(job: Job, q: Quote): { cycle_weeks: number | null; regular_pence: number | null; initial_pence: number | null } {
  const cycle: Record<HowOften, number | null> = { w2: 2, w4: 4, w6: 6, w8: 8, w12: 12, one_off: null }
  return {
    cycle_weeks: cycle[job.how_often],
    regular_pence: q.regular_range_pence ? null : q.regular_pence,
    initial_pence: q.first_range_pence ? null : q.first_pence,
  }
}

// A sent quote marks a new enquiry (no status yet) as quoted: the status New jobs' own Quoted column sets
// ("Quote"), so the board moves it there. Any status the owner chose is left alone: undefined = no change.
export function quotedStatus(current: string | null | undefined): string | undefined {
  return (current ?? '').trim() ? undefined : BOARD_COLUMNS.find((c) => c.key === 'quoted')!.status!
}

// The reply was changed by hand and the job has changed since: the words no longer match the price that
// would be saved, so nothing may be sent until it is written again from the price (Astra, 1 Oct). `builtFrom`
// is the reply as last written from the price, `now` the reply the job gives now.
export function replyOutOfDate(edited: boolean, builtFrom: string | null, now: string | null): boolean {
  return edited && builtFrom !== now
}

// ---- parked saves on the job --------------------------------------------------------------------------
// Every quote save is labelled with this, so the panel can tell its own parked saves from the owner's other
// changes to the same job (an address or notes edit waiting on New jobs).
export const QUOTE_LABEL = 'Quote: '

// Saves to this job that are parked (a clash, or refused) and would hold a new quote save behind them. The
// panel's own earlier quote saves may be cleared, since the quote on screen replaces them; any other parked
// change is the owner's to sort out on New jobs first, never thrown away from here (Astra, 1 Oct).
export function parkedOnJob(entries: readonly { key: string; path: string; label: string; status: string }[], path: string): { ours: string[]; others: number } {
  const parked = entries.filter((e) => e.path === path && (e.status === 'conflict' || e.status === 'failed'))
  return {
    ours: parked.filter((e) => e.label.startsWith(QUOTE_LABEL)).map((e) => e.key),
    others: parked.filter((e) => !e.label.startsWith(QUOTE_LABEL)).length,
  }
}

// ---- the job as the panel starts it -------------------------------------------------------------------
// Only what the job holds in its own columns: the postcode from the address, and how often from a cycle
// already saved (4, 8 or 12 weeks). Nothing is guessed from the enquiry's own words: windows, roof windows,
// house size and fronts only start empty, and the owner fills them in with the enquiry beside them (manager,
// 1 Oct, after guesses came out wrong but looked sure). The one reading of their words is a reply that names a
// number of windows and no other number (windowsFromWords, below), shown to the owner before anything is sent.

// The job as the panel starts it: how often may still be unpicked.
// What the panel says is missing before a price can be given, in the owner's words.
export function missingWords(q: Quote | null): string | null {
  if (!q) return 'Pick how often to see a price.'
  if (q.status === 'priced') return null
  if (q.missing.includes('sqm')) return 'Add the size in square metres to see a price.'
  return q.missing.includes('windows') ? 'Add the number of windows to see a price.' : 'Pick the house size to see a price.'
}

// ---- a reply that names the number of windows (Liam, 16 Oct 2026) ------------------------------------------
// "Hi Liam, there are 8 windows including the front door - H" -> 8. Only when the words hold ONE number, written
// right before "windows" ("8 windows", "eight windows"): "8 windows and 2 roof windows", "8 or 9 windows" or
// extra unrelated numbers read as nothing. Explicit bedroom counts are ignored (31 Oct, approved Offer too drawing).
// "Tuesday 21 October", the day offered in the reply.
function dayWords(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`)
  return d.toLocaleDateString(roundConfig().country === 'GB' ? 'en-GB' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })
}

// The answer to a reply that gave the number of windows, in Liam's own words (16 Oct 2026):
//   Thanks Hannah,
//   It would be £25 for a regular clean and £35 for the first clean to get the frames and sills and glass up to scratch.
//   I have availability on Tuesday 21 October if you'd like to arrange a clean?
//   Kind regards, Liam
// No rate a window is named: a small job is the smallest job price, not windows times the rate. `often` names how
// often only when the regular price depends on it. `day` is the next working day in the diary, or null for none.
export function repliedQuoteMessage(
  name: string | null | undefined,
  q: Quote,
  opts: { often?: HowOften | null; day?: string | null; offers?: string[] } = {},
  settings: BusinessSettings = businessSettings(),
): string | null {
  if (q.status !== 'priced' || q.regular_range_pence || q.first_range_pence) return null
  // An enquiry named after the text that made it ("Text from 07700 900123") has no name to thank.
  const first = /^(text|whatsapp) from /i.test(String(name ?? '').trim()) ? '' : firstName(name)
  const often = opts.often && opts.often !== 'one_off' ? ` ${OFTEN_WORDS[opts.often]}` : ''
  const price = q.first_pence === q.regular_pence
    ? `It would be ${quotePounds(q.regular_pence)} a clean${often}.`
    : `It would be ${quotePounds(q.regular_pence)} for a regular clean${often} and ${quotePounds(q.first_pence)} for the first clean to get the frames and sills and glass up to scratch.`
  const next = opts.day ? `I have availability on ${dayWords(opts.day)} if you'd like to arrange a clean?` : "Just let me know if you'd like to arrange a clean."
  return `${first ? `Thanks ${first},` : 'Thanks,'}\n${price}${opts.offers?.length ? `\n${opts.offers.join(' ')}` : ''}\n${next}\nKind regards, ${settings.booking_signoff}`
}
